/**
 * Utility for managing mobile notifications for time tracking
 */

export const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
        console.warn('Notifications are not supported by this browser.');
        return false;
    }

    if (Notification.permission === 'granted') {
        return true;
    }

    if (Notification.permission !== 'denied') {
        try {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }

    return false;
};

let lastUpdateTime = 0;

/**
 * Updates or shows the time tracking notification
 * @param {number} timeInSeconds - Elapsed time in seconds
 * @param {boolean} isBreak - Whether the user is on break
 * @param {string} sessionName - Optional name of the subproject or session type
 */
export const updateTimeNotification = async (timeInSeconds, isBreak = false, sessionName = "") => {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
        return;
    }

    // Update at most once per second to avoid flooding
    const now = Date.now();
    if (now - lastUpdateTime < 900) {
        return;
    }
    lastUpdateTime = now;

    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = timeInSeconds % 60;

    const timeStr = [
        hours.toString().padStart(2, '0'),
        minutes.toString().padStart(2, '0'),
        seconds.toString().padStart(2, '0')
    ].join(':');

    const title = isBreak ? 'Pause läuft' : 'Zeiterfassung läuft';
    let body = `Aktuelle Zeit: ${timeStr}`;
    if (sessionName) {
        body += `\n${sessionName}`;
    }

    const options = {
        body,
        icon: '/favicon.ico',
        tag: 'planyvo-time-tracker', // Group notifications
        renotify: false, // Don't vibrate/sound on every update
        silent: true,
        requireInteraction: true, // Keep notification visible
        data: {
            url: window.location.origin
        }
    };

    try {
        if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.ready;
            await registration.showNotification(title, options);
        } else {
            // Fallback for browsers without service worker active
            new Notification(title, options);
        }
    } catch (error) {
        console.error('Error showing notification:', error);
    }
};

/**
 * Closes any active time tracking notification
 */
export const closeTimeNotification = async () => {
    if (!('Notification' in window)) return;

    try {
        if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.ready;
            const notifications = await registration.getNotifications({ tag: 'planyvo-time-tracker' });
            notifications.forEach(notification => notification.close());
        }
    } catch (error) {
        console.error('Error closing notification:', error);
    }
};
